import {
	ALL_MODELS,
	BOX_SHADOWS,
	DROP_TYPES,
	FONTS,
	MODEL_STATES,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
	WORKER_TYPES,
} from "../../constants";

import { Button, TextField } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import Paper from "@material-ui/core/Paper";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import React from "react";
import { formatNumber } from "../../helpers/FormatNumber";
import { stateType, stateTypeF } from "../../state";
import { BoxHeader } from "../Common/BoxHeader";
import { HelpIcon } from "../Common/ToolTipIcon";
import { calcCustomTopicsDataInBkg } from "../NLP/textProcessorBkg";
import { ProgressBar } from "../Common/LoadingDialog";
import Dataset from "../../MachineLearning/Dataset";
import { applyFiltersToDash } from "../DataCleaning/filters";
import { datasetDataType } from "../NLP/textProcessor";
import { getWorker } from "../../MachineLearning/getWorker";
import { datasetFromJSON } from "../DataIngestion/DropZoneBox";
import { newObj } from "../../saveState";
import {
	FAILED_STATUS,
	SUCCESS_STATUS,
} from "../../MachineLearning/BackgroundTrainer";
import { handleError } from "../../App";

export const MAX_TOPICS = 5;

export type customTopicType = {
	id: number;
	selected: boolean;
	topicName: string;
	phrases: string;
	sentiment: number;
	nDocs: number;
};

export function getEmptyCustomTopic(count: number): customTopicType {
	return {
		id: count,
		selected: true,
		topicName: `Topic ${count + 1}`,
		phrases: "",
		sentiment: undefined,
		nDocs: undefined,
	};
}

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		container: {
			display: "flex",
			flexDirection: "column",
			width: "100%",
			backgroundColor: "white",
			boxShadow: BOX_SHADOWS,
			borderRadius: "10px",
		},
		root: {
			width: "100%",
		},
		paper: {
			width: "100%",
			marginBottom: theme.spacing(2),
		},
		table: {
			minWidth: 750,
		},
		visuallyHidden: {
			border: 0,
			clip: "rect(0 0 0 0)",
			height: 1,
			margin: -1,
			overflow: "hidden",
			padding: 0,
			position: "absolute",
			top: 20,
			width: 1,
		},
		trainButton: {},
		disabledButton: {
			"&:disabled": {
				backgroundColor: "red",
			},
		},
		fastTraining: {
			fontWeight: "normal",
			display: "flex",
			marginLeft: "30px",
			flexDirection: "row",
			width: "0px",
			lineHeight: "22px",
		},
		textField: {
			"& .MuiInputBase-input": {
				color: PRIMARY_COLOR, // Text color
			},
			"& .MuiInput-underline:before": {
				borderBottomColor: "rgba(0,0,0,1)", // Semi-transparent underline
			},
			"& .MuiInput-underline:hover:before": {
				borderBottomColor: SECONDARY_COLOR, // Solid underline on hover
			},
			"& .MuiInput-underline:after": {
				borderBottomColor: SECONDARY_COLOR, // Solid underline on focus
			},
		},
		textFieldInvis: {
			"& .MuiInputBase-input": {
				color: PRIMARY_COLOR, // Text color
			},
			"& .MuiInput-underline:before": {
				borderBottomColor: "rgba(0,0,0,0)", // Semi-transparent underline
			},
			"& .MuiInput-underline:hover:before": {
				borderBottomColor: "rgba(0,0,0,0)", // Solid underline on hover
			},
			"& .MuiInput-underline:after": {
				borderBottomColor: "rgba(0,0,0,0)", // Solid underline on focus
			},
		},
		newRow: {
			cursor: "pointer",
			"&:hover": { color: SECONDARY_COLOR },
		},
		close: {
			marginRight: "-13px",
			marginLeft: "13px",
			marginTop: "4px",
			width: "22px",
			cursor: "pointer",
			color: "rgba(0,0,0,0.7)",
			// transition: "0.2s",
			"&:hover": { transform: "scale(1.1)", color: SECONDARY_COLOR },
		},
	})
);

interface Data {
	calories: string;
	carbs: string;
	fat: number;
	name: string;
	protein: number;
}

interface HeadCell {
	disablePadding: boolean;
	id: keyof Data;
	label: string;
	numeric: boolean;
}

const headCells: HeadCell[] = [
	{
		id: "name",
		numeric: false,
		disablePadding: true,
		label: "Topics",
	},
	{
		id: "carbs",
		numeric: false,
		disablePadding: false,
		label: "Topic Phrases",
	},
	{
		id: "fat",
		numeric: false,
		disablePadding: false,
		label: "Topic Sentiment",
	},
	{
		id: "protein",
		numeric: false,
		disablePadding: false,
		label: "Doc. Count",
	},
];

interface EnhancedTableProps {
	classes: ReturnType<typeof useStyles>;
	selected: boolean[];
	// models: string[];
	onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
	state: stateTypeF;
}

function EnhancedTableHead(props: EnhancedTableProps) {
	const { classes, onSelectAllClick, selected, state } = props;

	let numSelected = state.checkedTopics.filter((v) => v).length;

	return (
		<TableHead>
			<TableRow>
				<div></div>
				<TableCell padding="checkbox">
					<Checkbox
						style={{ color: PRIMARY_COLOR }}
						indeterminate={
							numSelected > 0 && numSelected < selected.length
						}
						checked={numSelected === selected.length}
						onChange={onSelectAllClick}
						disabled={state.isTopicsProcessing}
					/>
				</TableCell>
				{headCells.map((headCell) => (
					<TableCell
						key={headCell.id}
						align={
							["carbs", "name"].includes(headCell.id)
								? "left"
								: "center"
						}
						padding={headCell.disablePadding ? "none" : "normal"}
						style={{
							fontFamily: FONTS,
							fontWeight: "bold",
							justifyContent: "center",
						}}
					>
						{headCell.id === "carbs" ? (
							<div style={{ display: "flex" }}>
								<div>{headCell.label}</div>
								<div style={{ marginLeft: "5px" }}>
									<HelpIcon
										title={
											"Comma separated list of phrases which define the topic."
										}
									></HelpIcon>
								</div>
							</div>
						) : (
							<div>{headCell.label}</div>
						)}
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

function EnhancedTable({ state }: stateType) {
	const classes = useStyles();

	const ndx = state.saveState.topicModeling.varSelected;
	const topicsData =
		state.saveState.dataset.visualizationData[ndx].customTopicsData;
	let customTopics = topicsData.customTopics;

	const handleSelectAllClick = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const ndx = state.saveState.topicModeling.varSelected;
		const visData = state.saveState.dataset.visualizationData[ndx];
		const colName = state.saveState.dataset.nlpColumns[ndx];
		const topics = visData.customTopicsData.customTopics;

		for (let topic of topics) {
			topic.selected = event.target.checked;
		}

		for (let i = 0; i < state.checkedTopics.length; i++) {
			state.checkedTopics[i] = event.target.checked;
		}
		state.setAllCheckedTopics(state.checkedTopics);

		const sentences = visData.customTopicsData.sentences;
		const cloudData = visData.topicCloudData.cloudData;

		// visData.customTopicsData = calcCustomTopicsData(
		// 	sentences,
		// 	topics,
		// 	cloudData,
		// 	visData.customTopicsData.plotPoints,
		// 	state.saveState.dataset.data[colName]
		// );

		// state.setSaveState(state.saveState, true);
		calcCustomTopicsDataInBkg(
			sentences,
			topics,
			cloudData,
			visData.customTopicsData.plotPoints,
			state.saveState.dataset.data[colName],
			state
		);
	};

	function handleSelect(id, index) {
		const ndx = state.saveState.topicModeling.varSelected;
		const visData = state.saveState.dataset.visualizationData[ndx];
		const topics = visData.customTopicsData.customTopics;
		const colName = state.saveState.dataset.nlpColumns[ndx];
		let topic = topics.filter((v) => v.id === id)[0];
		topic.selected = !topic.selected;
		state.setCheckedTopics(topic.selected, index);
		if (topic.phrases.length > 0) {
			const sentences = visData.customTopicsData.sentences;
			const cloudData = visData.topicCloudData.cloudData;

			calcCustomTopicsDataInBkg(
				sentences,
				topics,
				cloudData,
				visData.customTopicsData.plotPoints,
				state.saveState.dataset.data[colName],
				state
			);
		} else {
			state.setSaveState(state.saveState, true);
		}
	}

	function handleEnterTopicChange(e, id: number) {
		if (e.keyCode == 13) {
			handleTopicChange(e.target.value, id);
		}
	}

	function handleEnterNameChange(e, id: number) {
		if (e.keyCode == 13) {
			handleNameChange(e.target.value, id);
		}
	}
	function handleTopicChange(value: string, id: number) {
		const ndx = state.saveState.topicModeling.varSelected;
		const visData = state.saveState.dataset.visualizationData[ndx];
		const topics = visData.customTopicsData.customTopics;
		const colName = state.saveState.dataset.nlpColumns[ndx];
		let topic = topics.filter((v) => v.id === id)[0];
		if (topic.phrases === value) {
			return;
		}
		const sentences = visData.customTopicsData.sentences;
		const cloudData = visData.topicCloudData.cloudData;
		topic.phrases = value;

		if (topic.selected) {
			calcCustomTopicsDataInBkg(
				sentences,
				topics,
				cloudData,
				visData.customTopicsData.plotPoints,
				state.saveState.dataset.data[colName],
				state
			);
		}

		state.setSaveState(state.saveState, true);
	}

	function handleNameChange(name: string, id: number) {
		const ndx = state.saveState.topicModeling.varSelected;
		const visData = state.saveState.dataset.visualizationData[ndx];
		const topics = visData.customTopicsData.customTopics;
		let topic = topics.filter((v) => v.id === id)[0];

		// if (topic.topicName === name) {
		//   return;
		// }

		topic.topicName = name;
		state.setSaveState(state.saveState, true);
	}

	let selected = customTopics.map((r) => r.selected);
	return (
		<div className={classes.root}>
			<Paper className={classes.paper} elevation={0}>
				<TableContainer>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						size={"small"}
						aria-label="enhanced table"
					>
						<EnhancedTableHead
							classes={classes}
							selected={selected}
							onSelectAllClick={handleSelectAllClick}
							state={state}
						/>
						<TableBody>
							{customTopics.map((row, index) => {
								const isItemSelected =
									state.checkedTopics[index];
								const labelId = `enhanced-table-checkbox-${index}`;
								return (
									<TableRow
										hover
										role="checkbox"
										aria-checked={isItemSelected}
										tabIndex={-1}
									>
										<div></div>
										<TableCell padding="checkbox">
											<Checkbox
												style={{ color: PRIMARY_COLOR }}
												disabled={
													state.isTopicsProcessing
												}
												checked={isItemSelected}
												inputProps={{
													"aria-labelledby": labelId,
												}}
												onClick={() =>
													handleSelect(row.id, index)
												}
											/>
										</TableCell>
										<TableCell
											id={labelId}
											padding="none"
											style={{
												fontFamily: FONTS,
												width: "100px",
											}}
										>
											<TextField
												disabled={
													state.isTopicsProcessing
												}
												className={
													classes.textFieldInvis
												}
												inputProps={{
													spellCheck: "false",
												}}
												value={
													customTopics[index]
														.topicName
												}
												onChange={(e) => {
													customTopics[
														index
													].topicName =
														e.target.value;
													state.setSaveState(
														state.saveState
													);
												}}
												onBlur={(e) =>
													handleNameChange(
														e.target.value,
														row.id
													)
												}
												onKeyDown={(e) =>
													handleEnterNameChange(
														e,
														row.id
													)
												}
												style={{
													height: "25px",
													marginTop: "-5px",
													width: "150px",
												}}
											></TextField>
										</TableCell>
										<TableCell align="left" id={labelId}>
											<TextField
												disabled={
													state.isTopicsProcessing
												}
												className={classes.textField}
												inputProps={{
													spellCheck: "false",
												}}
												onBlur={(e) =>
													handleTopicChange(
														e.target.value,
														row.id
													)
												}
												onKeyDown={(e) =>
													handleEnterTopicChange(
														e,
														row.id
													)
												}
												value={
													state.enteredPhrases[index]
												}
												onChange={(e) => {
													state.setEnteredPhrases(
														e.target.value,
														index
													);
												}}
												style={{
													height: "25px",
													marginTop: "-5px",
													width: "100%",
												}}
											></TextField>
										</TableCell>
										<TableCell
											align="center"
											style={{
												fontFamily: FONTS,
												width: "200px",
												justifyContent: "center",
											}}
										>
											<div style={{ marginLeft: "5px" }}>
												{row.sentiment !== undefined
													? Math.round(row.sentiment)
													: "-"}
											</div>
										</TableCell>
										<TableCell
											align="center"
											style={{
												fontFamily: FONTS,
												justifyContent: "center",
											}}
										>
											<div style={{ marginLeft: "5px" }}>
												{row.nDocs !== undefined
													? formatNumber(row.nDocs)
													: "-"}
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</div>
	);
}

export function CustomTopicTable({ state }: stateType) {
	const classes = useStyles();

	const valToPredict = state.saveState.modelTraining.valueToPredict;
	const col = state.saveState.dataset.getColumn(valToPredict);
	if (col === undefined) {
		return <div></div>;
	}
	async function handleApplyToDash() {
		state.setIsLoadingPopupOpen(true);
		let customTopics = state.saveState.dataset.visualizationData.map(
			(v) => v.customTopicsData.customTopics
		);
		const payload = {
			workerType: WORKER_TYPES.applyTopics,
			customTopics: customTopics,
			dataset: JSON.stringify(state.saveState.dataset),
			filters: state.saveState.cleanData.cleanDataFiltersApplied,
			rowsToDrop: state.saveState.cleanData.allRowsDropped,
		};

		const worker = getWorker();

		let dataset;

		let resolves = {};
		let workerStatus = FAILED_STATUS;
		worker.onmessage = function (message) {
			if (message.data?.status === SUCCESS_STATUS) {
				dataset = message.data.datasetJSON;
				resolves[0]();
				delete resolves[0];
				workerStatus = SUCCESS_STATUS;
			} else {
				handleError(
					state,
					message.data?.error,
					WORKER_TYPES.applyTopics
				);
				resolves[0]();
				delete resolves[0];
			}
		};

		await new Promise(function (resolve, reject) {
			resolves[0] = resolve;
			worker.postMessage(payload);
		});
		if (workerStatus === FAILED_STATUS) {
			return;
		}
		// Reset Model Values
		const modelVals = state.saveState.modelTraining;

		modelVals.modelStates = newObj(
			Object.values(ALL_MODELS),
			MODEL_STATES.ready
		);

		modelVals.totalTrainProgress = 0;
		modelVals.modelProgress = newObj(Object.values(ALL_MODELS), 0);
		state.saveState.savedModels = [];
		state.saveState.dataset = datasetFromJSON(dataset);
		state.setSaveState(state.saveState, true);
		state.setReCalcSentTable(true);
		state.setIsLoadingPopupOpen(false);
	}

	const ndx = state.saveState.topicModeling.varSelected;
	const varName = state.saveState.dataset.nlpColumns[ndx];
	return (
		<div className={classes.container}>
			<BoxHeader
				title={`Topic Creation for '${varName}'`}
				toolTip={`A list of topics to group your text data by. This can help the machine learning models understand your text data or be used as a tool for your analysis.`}
			></BoxHeader>

			<EnhancedTable state={state}></EnhancedTable>
			<div
				style={{
					display: "flex",
					flexDirection: "row",
				}}
			>
				<div
					style={{
						marginLeft: "20px",
						marginTop: "00px",
						fontFamily: FONTS,
						fontWeight: "bold",
					}}
				></div>
				<div
					style={{
						// marginLeft: "auto",
						// marginRight: "20px",
						display: "flex",
						flexDirection: "row",
						width: "100%",
					}}
				>
					<div
						style={{
							width: "200px",
							visibility: state.isTopicsProcessing
								? "visible"
								: "hidden",
						}}
					>
						<div style={{ fontFamily: FONTS, marginTop: "10px" }}>
							Processing...
						</div>
						<ProgressBar
							progress={1}
							isDisabled={false}
						></ProgressBar>
					</div>
					<Button
						variant="contained"
						color="primary"
						style={{
							fontFamily: FONTS,
							fontWeight: "bold",
							width: "190px",
							height: "40px",
							marginLeft: "auto",
							marginRight: "20px",
							backgroundColor: SECONDARY_COLOR,
							marginBottom: "20px",
							color: state.isTopicsProcessing
								? "rgba(255,255,255,0.6)"
								: "white",
						}}
						onClick={handleApplyToDash}
						className={classes.trainButton}
						disabled={state.isTopicsProcessing}
					>
						{"Apply to Dashboard"}
					</Button>
				</div>
			</div>
		</div>
	);
}
